import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';

interface SiteHeaderProps {
  overlay?: boolean;
}

const navItems = [
  { label: '首页', to: '/' },
  { label: '分类', to: '/category' },
  { label: '热门榜单', to: '/category/all?sort=hot' },
  { label: '完本佳作', to: '/category/all?status=complete' },
  { label: '关于我们', to: '/about' },
];

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const transparent = overlay && !scrolled;
  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/category/all?q=${encodeURIComponent(q)}` : '/category/all');
    setMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        transparent ? 'bg-transparent' : 'border-b border-background-200 bg-background-50/95 backdrop-blur-md'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:h-20 md:px-6">
        <Link to="/" className="group flex items-center gap-2.5" aria-label="57漫画网首页">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-lg md:h-10 md:w-10 ${
              transparent ? 'bg-primary-500' : 'bg-primary-500'
            }`}
          >
            <i className="ri-book-2-fill h-5 w-5 text-background-50" aria-hidden="true"></i>
          </span>
          <span
            className={`font-display text-xl font-bold tracking-wide md:text-2xl ${
              transparent ? 'text-background-50' : 'text-foreground-900'
            }`}
          >
            57漫画网
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex lg:gap-2" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `relative whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors md:px-3.5 ${
                  transparent
                    ? 'text-background-50/90 hover:bg-background-50/10 hover:text-background-50'
                    : 'text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
                } ${isActive && !transparent ? 'font-bold text-primary-600' : ''}`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <span
                      className={`absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full ${
                        transparent ? 'bg-background-50' : 'bg-primary-500'
                      }`}
                      aria-hidden="true"
                    ></span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form
            onSubmit={handleSearch}
            className={`hidden items-center gap-1 rounded-full border px-3 py-1.5 lg:flex ${
              transparent
                ? 'border-background-50/40 bg-background-50/10 focus-within:border-background-50/70'
                : 'border-background-300 bg-background-100 focus-within:border-primary-400'
            }`}
            role="search"
            aria-label="搜索漫画"
          >
            <i
              className={`ri-search-line h-4 w-4 ${transparent ? 'text-background-50/80' : 'text-foreground-500'}`}
              aria-hidden="true"
            ></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索漫画 / 韩漫"
              className={`w-36 bg-transparent text-sm outline-none placeholder:text-xs lg:w-44 ${
                transparent ? 'text-background-50 placeholder:text-background-50/60' : 'text-foreground-900 placeholder:text-foreground-400'
              }`}
              aria-label="搜索关键词"
            />
          </form>
          <button
            type="button"
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
              transparent ? 'bg-background-50/10 text-background-50 hover:bg-background-50/20' : 'bg-secondary-100 text-secondary-900 hover:bg-secondary-200'
            }`}
            aria-label="用户中心"
          >
            <i className="ri-user-3-line h-4 w-4" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors md:hidden ${
              transparent ? 'bg-background-50/10 text-background-50' : 'bg-secondary-100 text-secondary-900'
            }`}
            aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
            aria-expanded={menuOpen}
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} h-5 w-5`} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 md:hidden">
          <form onSubmit={handleSearch} className="mb-3 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2" role="search" aria-label="搜索漫画">
            <i className="ri-search-line h-4 w-4 text-foreground-500" aria-hidden="true"></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索漫画 / 韩漫"
              className="w-full bg-transparent text-sm outline-none placeholder:text-xs"
              aria-label="搜索关键词"
            />
          </form>
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line h-4 w-4 text-foreground-400" aria-hidden="true"></i>
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}